<!-- SessionCard.vue -->
<!-- Displays a single session in the session list sidebar. -->
<!-- Shows thumbnail, risk badge, protocol tag, format toggle and duration. -->
<template>
  <div
    class="video-item bg-dark-tertiary overflow-hidden border transition-all rounded"
    :class="isSelected ? 'selected' : ''"
    @click="select"
  >
    <!-- Thumbnail -->
    <div class="relative aspect-video bg-dark-primary overflow-hidden group">

      <img
        v-if="thumbUrl"
        :src="thumbUrl"
        class="absolute inset-0 w-full h-full object-cover"
        alt=""
        @load="thumbLoaded = true"
        @error="handleThumbError"
      />

      <!-- Placeholder while loading -->
      <div
        v-if="!thumbLoaded"
        class="absolute inset-0 bg-black flex items-center justify-center"
      >
        <div class="text-center px-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48" height="48" viewBox="0 0 24 24"
            fill="none" stroke="currentColor"
            stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
            class="mx-auto mb-3 text-gray-600 opacity-50"
            :class="{ 'animate-pulse': loadingThumb }"
          >
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
          </svg>
          <p class="text-xs text-gray-500 font-medium">
            {{ loadingThumb ? "Loading preview…" : "No preview available" }}
          </p>
        </div>
      </div>

      <!-- Hover overlay -->
      <div
        class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
      >
        <Play class="w-6 h-6 text-white" />
      </div>

      <!-- Risk badge (top-left) -->
      <div
        v-if="session.risk"
        class="risk-badge"
        :class="'risk-' + (session.risk ?? '').toLowerCase()"
      >
        {{ riskLabel }}
      </div>

      <!-- Format toggle (top-right) -->
      <div class="format-toggle">
        <button
          class="format-button"
          :class="{
            active:          currentFormat === 'hls' && hlsReady,
            'selected-card': isSelected && hlsReady,
            'hls-pending':   !hlsReady,
          }"
          :style="!hlsReady ? 'pointer-events: none' : ''"
          :title="hlsReady ? 'HLS' : 'HLS not ready'"
          @click.stop="hlsReady && handleFormatClick('hls')"
        >
          HLS
        </button>
        <button
          class="format-button"
          :class="{
            active:          currentFormat === 'guac',
            'selected-card': isSelected,
            disabled:        !isSelected,
          }"
          @click.stop="handleFormatClick('guac')"
        >
          GUAC
        </button>
      </div>

      <!-- Duration badge (bottom-right) -->
      <div class="video-duration font-tabular">
        {{ durationFormatted }}
      </div>
    </div>

    <!-- Card body -->
    <div class="p-3">
      <h3
        class="text-dark-primary font-medium text-xs mb-2 truncate"
        :title="session.id"
      >
        {{ session.id }}
      </h3>

      <div class="flex flex-wrap gap-2">
        <span
          v-for="tag in tags"
          :key="tag.label"
          class="session-tag"
          :class="tag.class"
          :title="tag.tooltip"
        >
          {{ tag.label }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { Play } from "lucide-vue-next";
import { useSessionStore } from "../stores/sessionStore";
import { getAuthenticatedImageUrl } from "../services/apiClient";

const props = defineProps({
  session: { type: Object, required: true },
});

const sessions = useSessionStore();

const isSelected = computed(
  () => sessions.currentSession?.id === props.session.id
);

function select() {
  sessions.selectSession(props.session);
}

// ---------------------------------------------------------------------------
// Thumbnail
// ---------------------------------------------------------------------------

const thumbLoaded  = ref(false);
const loadingThumb = ref(false);
const thumbUrl     = ref(null);
let   objectUrl    = null;

async function loadThumbnail() {
  const frameId = props.session.first_frame_id;
  if (frameId === null || frameId === undefined) return;

  loadingThumb.value = true;
  try {
    const url = await getAuthenticatedImageUrl(
      `/sessions/${props.session.id}/frames/${frameId}.png`,
      { silent: true }
    );
    if (url) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = url;
      thumbUrl.value = url;
    }
  } finally {
    loadingThumb.value = false;
  }
}

function handleThumbError() {
  thumbLoaded.value = false;
  thumbUrl.value    = null;
}

// ---------------------------------------------------------------------------
// Format toggle (HLS ↔ GUAC)
// ---------------------------------------------------------------------------

const hlsReady = computed(() =>
  props.session.hls?.status === "completed"
);

const currentFormat = computed(() =>
  sessions.getSessionFormat(props.session.id)
);

function handleFormatClick(fmt) {
  if (!isSelected.value || currentFormat.value === fmt) return;
  if (fmt === 'hls' && !hlsReady.value) return;

  sessions.setSessionFormat(props.session.id, fmt);

  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent("player-format-changed", {
        detail: { session: props.session, newFormat: fmt },
      })
    );
  }, 50);
}

// ---------------------------------------------------------------------------
// Duration
// ---------------------------------------------------------------------------

const riskLabel = computed(() => {
  const map = { critical: "CRIT", high: "HIGH", medium: "MED", low: "LOW" };
  return map[(props.session.risk ?? "").toLowerCase()] ?? props.session.risk;
});

const durationFormatted = computed(() => {
  const ms = props.session.guac?.duration_ms ?? 0;
  if (!ms) return "--:--";
  const total = Math.floor(ms / 1000);
  const m     = Math.floor(total / 60);
  const s     = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
});

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

const tags = computed(() => {
  const result = [];
  const id     = (props.session.id ?? "").toLowerCase();
  const fname  = (props.session.filename ?? "").toLowerCase();

  if (fname.includes("ssh") || id.includes("ssh"))       result.push({ label: "SSH",  class: "tag-ssh",  tooltip: "SSH session"  });
  else if (fname.includes("vnc") || id.includes("vnc"))  result.push({ label: "VNC",  class: "tag-vnc",  tooltip: "VNC session"  });
  else                                                    result.push({ label: "RDP",  class: "tag-rdp",  tooltip: "RDP session"  });

  const status = props.session.status;
  if (status === "processing") result.push({ label: "PROCESSING", class: "tag-activity", tooltip: "Pipeline running" });
  else if (status === "failed") result.push({ label: "FAILED",    class: "tag-error",    tooltip: "Pipeline failed"  });

  const cmdCount = props.session.llm?.commands ?? 0;
  if (cmdCount > 0) {
    result.push({ label: `CMD:${cmdCount}`, class: "tag-cmds", tooltip: `${cmdCount} commands detected` });
  }

  return result;
});

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(() => {
  loadThumbnail();
  if (!hlsReady.value) {
    sessions.setSessionFormat(props.session.id, "guac");
  }
});

onUnmounted(() => {
  if (objectUrl) URL.revokeObjectURL(objectUrl);
});

watch(() => props.session.id, () => {
  thumbLoaded.value = false;
  thumbUrl.value    = null;
  loadThumbnail();
});

watch(() => props.session.first_frame_id, (newId) => {
  if (newId !== null && newId !== undefined && !thumbUrl.value) {
    loadThumbnail();
  }
});
</script>

<style scoped>
.video-item {
  cursor: pointer;
  border: 1px solid var(--border-color);
  transition: border 0.2s ease;
}

.video-item.selected {
  border-color: var(--accent);
}

/* Risk badge */
.risk-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  border: 1px solid;
}

.risk-low      { background: rgba(16,185,129,.15); border-color: rgba(16,185,129,.45); color: #6ee7b7; }
.risk-medium   { background: rgba(245,158,11,.15); border-color: rgba(245,158,11,.45); color: #fcd34d; }
.risk-high     { background: rgba(239,68,68,.15);  border-color: rgba(239,68,68,.45);  color: #fca5a5; }
.risk-critical { background: rgba(239,68,68,.3);   border-color: rgba(239,68,68,.75);  color: #ef4444; }

/* Format toggle */
.format-toggle {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  border-radius: 0.25rem;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
}

.format-button {
  width: 52px;
  font-size: 10px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.4);
  background: transparent;
  border: none;
  cursor: pointer;
}

.format-button.active {
  background: rgba(255, 255, 255, 0.15);
}

.format-button.active.selected-card {
  background: var(--accent);
  color: #fff;
}

.format-button.disabled {
  pointer-events: none;
}

.format-button.hls-pending {
  opacity: 0.3;
  cursor: not-allowed;
  pointer-events: none;
}

/* Duration badge */
.video-duration {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 0.25rem 0.5rem;
  font-size: 11px;
  font-weight: 600;
  border-radius: 0.25rem;
}
</style>